import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "../lib/api";
import { isLegacyAuthMode } from "../lib/env";
import { supabase } from "../lib/supabase";
import type { ImportJobDetail, UploadImportPreview } from "../api/imports";

export default function ImportPdfReview({ job, children }: {
  job: ImportJobDetail;
  children: (job: ImportJobDetail, onPage: (page: number) => void) => ReactNode;
}) {
  const [page, setPage] = useState(1);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [pageError, setPageError] = useState(false);
  const [renderedPageCount, setRenderedPageCount] = useState<number | null>(null);
  const preview = useQuery({
    queryKey: ["import-review-preview", job.job_id, job.status, job.updated_at],
    queryFn: () => fetchJson<UploadImportPreview>(`/api/imports/${job.job_id}/preview`),
    retry: false,
  });
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setPdfUrl(null);
    setPdfError(false);
    setPage(1);
    async function load() {
      const headers: Record<string, string> = {};
      if (!isLegacyAuthMode()) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) headers.Authorization = `Bearer ${session.access_token}`;
      }
      const response = await fetch(`/api/imports/${job.job_id}/pdf`, {
        headers, credentials: "same-origin", signal: controller.signal,
      });
      if (!response.ok) throw new Error("PDF unavailable");
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);
    }
    void load().catch(() => { if (!controller.signal.aborted) setPdfError(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [job.job_id, job.status, job.updated_at]);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setPageUrl(null);
    setPageError(false);
    async function loadPage() {
      const headers: Record<string, string> = {};
      if (!isLegacyAuthMode()) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) headers.Authorization = `Bearer ${session.access_token}`;
      }
      const response = await fetch(`/api/imports/${job.job_id}/page.png?page=${page}`, {
        headers, credentials: "same-origin", signal: controller.signal,
      });
      if (!response.ok) throw new Error("Page unavailable");
      const count = Number(response.headers.get("X-PDF-Pages"));
      if (!controller.signal.aborted && count > 0) setRenderedPageCount(count);
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      setPageUrl(objectUrl);
    }
    void loadPage().catch(() => { if (!controller.signal.aborted) setPageError(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [job.job_id, job.updated_at, page]);
  const detail = preview.data ? { ...job, parse_result: { ...job.parse_result, ...preview.data } } : job;
  const pageCount = renderedPageCount ?? preview.data?.page_count ?? (job.parse_result as UploadImportPreview | null)?.page_count;
  const missingOriginal = job.status === "review" && preview.data?.review_file_available === false;
  return <div className="grid min-h-full gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
    <section aria-label="Original-PDF" className="flex min-h-80 min-w-0 flex-col rounded-lg border border-slate-200 bg-slate-50 lg:sticky lg:top-[var(--app-header-h,0px)] lg:h-[calc(100dvh-10rem)]">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2 text-sm">
        <span className="font-medium">Original-PDF · Seite {page}</span>
        {pdfUrl && <a href={`${pdfUrl}#page=${page}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline">PDF öffnen</a>}
      </div>
      <div className="flex items-center justify-center gap-4 border-b border-slate-200 p-2 text-sm">
        <button type="button" className="h-9 rounded-md px-3 hover:bg-slate-100 disabled:text-slate-400" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Vorherige Seite</button>
        <span>{page}{pageCount ? ` / ${pageCount}` : ""}</span>
        <button type="button" className="h-9 rounded-md px-3 hover:bg-slate-100 disabled:text-slate-400" disabled={pageError || (pageCount != null && page >= pageCount)} onClick={() => setPage(value => value + 1)}>Nächste Seite</button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {missingOriginal ? <p role="status" className="p-4 text-sm text-slate-600">Das Original-PDF fehlt. Wähle unter „Original ergänzen“ die ursprüngliche Datei aus.</p> : pageUrl ? <img alt={`Originalbeleg, Seite ${page}`} src={pageUrl} className="h-auto w-full" />
          : <p role="status" className="p-4 text-sm text-slate-600">{pageError ? "Diese PDF-Seite ist nicht verfügbar." : "PDF-Seite wird geladen …"}</p>}
        {pdfError && !missingOriginal && <p className="p-3 text-sm text-slate-600">Das Original-PDF konnte nicht geladen werden.</p>}
      </div>
    </section>
    <div className="min-w-0">
      {preview.isLoading && <p role="status" className="mb-3 text-sm text-slate-500">Erkannte Werte werden geladen …</p>}
      {preview.isError && <div role="status" className="mb-3 space-y-3 text-sm text-slate-600"><p>Original und Prüfdaten konnten nicht geprüft werden. Bitte lade die Vorschau erneut, bevor du fortfährst.</p><button type="button" onClick={() => void preview.refetch()} className="h-9 rounded-md border border-slate-200 bg-white px-3 font-medium text-slate-700 shadow-sm">Vorschau erneut laden</button></div>}
      <div hidden={job.status === "review" && (preview.isLoading || preview.isError)}>{children(detail, setPage)}</div>
    </div>
  </div>;
}
